/* ============================================================
   dispo-scroll.js — scroll behaviour for Horizon
   ------------------------------------------------------------
   SAVE AS:  assets/dispo-scroll.js
   (Explorer → assets → the "new file" icon at the top of the
    EXPLORER panel → name it dispo-scroll.js)

   THEN LOAD IT — in layout/theme.liquid, on the line directly
   below your AOS <script> tag in the <head>:

     <script src="{{ 'dispo-scroll.js' | asset_url }}" defer></script>

   Do NOT paste this into an existing Horizon asset file. Horizon's
   JS files are ES modules tied to its web components, and edits
   there get overwritten on theme update.
   ============================================================ */

(function () {
  'use strict';

  var isMobile =
    window.matchMedia('(max-width: 749px)').matches ||
    window.matchMedia('(pointer: coarse)').matches;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* 5 · Mobile / reduced-motion flags -------------------------
     Sets classes on <html> so CSS can switch effects off without
     any further JS. Use .is-mobile and .reduce-motion in your CSS
     to disable parallax, blur, and long transitions.            */
  var root = document.documentElement;
  root.classList.toggle('is-mobile', isMobile);
  root.classList.toggle('is-desktop', !isMobile);
  root.classList.toggle('reduce-motion', reduceMotion);

  var heavyOK = !isMobile && !reduceMotion;

  /* 1 · 'scrolled' class on <body> past 100px ------------------ */
  var scrolled = false;
  function onScroll() {
    var past = window.scrollY > 100;
    if (past !== scrolled) {
      scrolled = past;
      document.body.classList.toggle('scrolled', past);
    }
  }

  /* rAF throttle so the listener never fires more than once per frame */
  var ticking = false;
  window.addEventListener('scroll', function () {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      onScroll();
      if (heavyOK) parallax();
      ticking = false;
    });
  }, { passive: true });

  /* 2 · Section enter/exit detection --------------------------
     Adds .in-view to any element with [data-section-watch] and
     fires a 'dispo:section-enter' event you can hook elsewhere. */
  var watcher = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var el = entry.target;
      el.classList.toggle('in-view', entry.isIntersecting);
      if (entry.isIntersecting) {
        el.dispatchEvent(new CustomEvent('dispo:section-enter', {
          bubbles: true,
          detail: { name: el.dataset.sectionWatch || null }
        }));
      }
    });
  }, { threshold: 0.25, rootMargin: '0px 0px -10% 0px' });

  /* 3 · Staggered delays on grid children ---------------------
     Put data-stagger on any grid wrapper. Children get
     data-aos-delay of 0, 100, 200… capped so late items don't
     sit invisible for two seconds.                              */
  function applyStagger(scope) {
    (scope || document).querySelectorAll('[data-stagger]').forEach(function (grid) {
      var step = parseInt(grid.dataset.stagger, 10) || 100;
      var cap = 600;
      Array.prototype.forEach.call(grid.children, function (child, i) {
        if (!child.hasAttribute('data-aos')) {
          child.setAttribute('data-aos', grid.dataset.staggerAnim || 'fade-up');
        }
        child.setAttribute('data-aos-delay', String(Math.min(i * step, cap)));
        child.style.setProperty('--stagger-index', i);
      });
    });
  }

  /* 4 · Smooth scroll for internal anchors --------------------
     Honours reduced-motion, and offsets for Horizon's sticky
     header so targets don't land underneath it.                */
  function headerOffset() {
    var header = document.querySelector('.header, header[role="banner"], .section-header');
    return header ? header.getBoundingClientRect().height + 12 : 12;
  }

  document.addEventListener('click', function (e) {
    var link = e.target.closest('a[href^="#"]');
    if (!link) return;
    var id = link.getAttribute('href');
    if (!id || id === '#' || id.length < 2) return;

    var target = document.querySelector(id);
    if (!target) return;

    e.preventDefault();
    var top = target.getBoundingClientRect().top + window.scrollY - headerOffset();
    window.scrollTo({ top: top, behavior: reduceMotion ? 'auto' : 'smooth' });
    history.pushState(null, '', id);
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  });

  /* 6 · Parallax — desktop only, transform only ---------------
     Add data-parallax="0.15" to a background element. Higher
     number = more movement. Runs inside the rAF above.          */
  var parallaxEls = [];
  function collectParallax(scope) {
    parallaxEls = Array.prototype.slice.call(
      document.querySelectorAll('[data-parallax]')
    );
  }
  function parallax() {
    var vh = window.innerHeight;
    parallaxEls.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > vh) return;
      var rate = parseFloat(el.dataset.parallax) || 0.15;
      var offset = (rect.top - vh / 2) * rate;
      el.style.transform = 'translate3d(0,' + offset.toFixed(2) + 'px,0)';
    });
  }

  /* ---- wiring ---------------------------------------------- */
  function init(scope) {
    applyStagger(scope);
    collectParallax();
    (scope || document).querySelectorAll('[data-section-watch]')
      .forEach(function (el) { watcher.observe(el); });
    if (window.AOS) window.AOS.refreshHard();
    onScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Shopify re-renders sections in the theme editor — re-init */
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });

  window.DispoScroll = { init: init, isMobile: isMobile, heavyOK: heavyOK };
})();